import {
  Badge,
  Button,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { CalendarIcon, Pencil } from "lucide-react";
import type { OTRuleListItem } from "@/modules/hr/overtime/domain/types";

const dayTypeLabel: Record<OTRuleListItem["dayType"], string> = {
  weekday: "ວັນທຳມະດາ",
  holiday: "ວັນພັກ",
};

function formatDateRange(from: string, to: string | null): string {
  const fromStr = new Date(from).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  if (!to) return `${fromStr} → Permanent`;
  const toStr = new Date(to).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  return `${fromStr} → ${toStr}`;
}

type OTRulesTableProps = {
  data: OTRuleListItem[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onView?: (row: OTRuleListItem) => void;
  onEdit: (row: OTRuleListItem) => void;
};

export function OTRulesTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onView,
  onEdit,
}: OTRulesTableProps) {
  const columns: TanstackReactTable.ColumnDef<OTRuleListItem>[] = [
    createSortableColumn<OTRuleListItem>("name", "ປະເພດວັນ", {
      size: 180,
      cell: ({ row }) => (
        <div>
          <div>
            {onView ? (
              <button
                type="button"
                className="font-medium text-primary hover:underline"
                onClick={() => onView(row.original)}
              >
                {row.original.name}
              </button>
            ) : (
              <span className="font-medium">{row.original.name}</span>
            )}
          </div>
          <div className="text-muted-foreground text-xs">
            {dayTypeLabel[row.original.dayType]}
          </div>
        </div>
      ),
    }),
    createSortableColumn<OTRuleListItem>("rateMultiplier", "ຕົວຄູນ", {
      size: 120,
      cell: ({ row }) => (
        <span className={row.original.isActive ? "font-medium text-primary" : "text-muted-foreground"}>
          {row.original.rateMultiplier}x
        </span>
      ),
    }),
    createSortableColumn<OTRuleListItem>("effectiveFrom", "ໄລຍະເວລາ", {
      size: 220,
      cell: ({ row }) => (
        <span className="inline-flex items-center gap-1 text-muted-foreground text-sm">
          <CalendarIcon className="h-4 w-4" />
          {formatDateRange(row.original.effectiveFrom, row.original.effectiveTo)}
        </span>
      ),
    }),
    createSortableColumn<OTRuleListItem>("isActive", "ສະຖານະ", {
      size: 100,
      cell: ({ row }) => (
        <Badge variant={row.original.isActive ? "default" : "secondary"}>
          {row.original.isActive ? "ACTIVE" : "INACTIVE"}
        </Badge>
      ),
    }),
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(row.original)}
          aria-label="ແກ້ໄຂກົດການ"
        >
          <Pencil className="h-4 w-4" />
        </Button>
      ),
      size: 80,
    },
  ];

  return (
    <DataTable<OTRuleListItem, unknown>
          noDataMessage="ບໍ່ພົບກົດການ OT."
          isLoading={isLoading}
          columns={columns}
          data={data}
          offset={offset}
          limit={limit}
          totalCount={totalCount}
          onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortingChange={(sorting) => {
            if (sorting[0]?.id === "") return;
            onSortingChange(sorting[0]?.id ?? "effectiveFrom", !!sorting[0]?.desc);
          }}
        />
  );
}
